<template>
  <section ref="deviceAlerts" class="device-alerts-container">
    <div v-if="siteDevices" class="device-summary">
      <div class="summary-card summary-card--pads">
        <div class="summary-card__total">
          <span class="summary-card__label">{{ $t('total') }} {{ $t('pad') }}</span>
          <span class="summary-card__value">{{ deviceSummary.pad.total }}</span>
        </div>
        <div class="summary-card__offline-row">
          <div class="summary-card__offline">
            <span class="summary-card__label">{{ $t('offline') }} {{ $t('pad') }}</span>
            <span class="summary-card__value">{{ deviceSummary.pad.offline }}</span>
          </div>
          <div class="summary-card__offline">
            <span class="summary-card__label">{{ $t('offline') }} {{ $t('pad_counter') }}</span>
            <span class="summary-card__value">{{ deviceSummary.pad_counter.offline }}</span>
          </div>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-card__total">
          <span class="summary-card__label">{{ $t('total') }} {{ $t('counter') }}</span>
          <span class="summary-card__value">{{ deviceSummary.counter.total }}</span>
        </div>
        <div class="summary-card__offline-row">
          <div class="summary-card__offline">
            <span class="summary-card__label">{{ $t('offline') }} {{ $t('counter') }}</span>
            <span class="summary-card__value">{{ deviceSummary.counter.offline }}</span>
          </div>
        </div>
      </div>

      <div class="summary-card">
        <div class="summary-card__total">
          <span class="summary-card__label">{{ $t('total') }} {{ $t('tablet') }}</span>
          <span class="summary-card__value">{{ deviceSummary.tablet.total }}</span>
        </div>
        <div class="summary-card__offline-row">
          <div class="summary-card__offline">
            <span class="summary-card__label">{{ $t('offline') }} {{ $t('tablet') }}</span>
            <span class="summary-card__value">{{ deviceSummary.tablet.offline }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="tabs-toolbar">
      <transition name="fade">
        <div class="custom-button-container">
          <span>
            <TableSearch ref="tableSearch" :tableTitles="tableTitles" @input="handleInput" />
          </span>
          <button class="export-button-container" @click="openFullScreen">
            <img :src="require('@/assets/full_screen.svg')" alt="full screen" />
          </button>
        </div>
      </transition>

      <Tabs :isRTL="isRTL" :syncRoute="true">
        <Tab :label="$t('device-alerts')" to="/admin/device-alerts" :exact="true" class="device-alerts">
          <div class="table-header">
            <TableCell
              v-for="fieldName in fieldNames"
              ref="sortHeader"
              :isActive="fieldName === 'pad' && !!siteDevices"
              :isIgnored="fieldName === 'pinned' || fieldName === 'no.'"
              :key="fieldName"
              :item="fieldName"
              :isHeader="true"
              @cellClick="handleSortClick"
            />
          </div>
          <template v-if="activeTab === 'device-alerts'">
            <Loading v-if="!siteDevices" class="loader" />
            <div
              v-else
              v-for="(site, idx) in siteDevicesToShow"
              :key="site.site_id"
              @click="routeToSiteDevices(site)"
              class="table-row"
              :class="{ pinned: site.isPinned }"
            >
              <p>
                <Checkbox @click.native.stop :value="site.isPinned" @input="handlePin(site, $event)"></Checkbox>
              </p>
              <p>{{ idx + 1 }}.</p>
              <p class="table-cell text-cell">
                <span>{{ site.company.name }}</span>
              </p>
              <p class="table-cell text-cell">
                <span>{{ site.site }}</span>
              </p>
              <p class="room-name">{{ site.room }}</p>
              <span
                v-for="([deviceType, device]) in Object.entries(site).filter(
                  ([key, value]) => key !== 'sensor' && key !== 'qr_tag' && key !== 'qr_task' && typeof value === 'object' && !value.name
                )"
                :key="deviceType"
                class="device-container"
              >
                <DeviceStatus v-if="device.total" :status="device.err" isCount isError />
              </span>
              <p>
                {{ site.first_date !== 'N/A' ? new Date(site.first_date).toLocaleDateString('en-GB') : site.first_date }}
              </p>
              <p>{{ site.site_country }}</p>
              <p class="table-cell text-cell">
                <span>{{ site.site_state }}</span>
              </p>
              <p class="table-cell text-cell">
                <span>{{ site.site_city }}</span>
              </p>
            </div>
            <div class="table-row sum">
              <p>{{ $t('total_sites') }}:</p>
              <template v-for="idx in 3">
                <p :key="`alerts-spacer-${idx}`"></p>
              </template>
              <p class="room-name">{{ sitesTotal('room') }}</p>
              <span
                v-for="type in ['pad', 'pad_counter', 'tablet', 'counter']"
                :key="type"
                class="device-container"
              >
                <DeviceStatus :status="sitesTotal(type)" isCount isError />
              </span>
              <template v-for="idx in 4">
                <p :key="`alerts-tail-${idx}`"></p>
              </template>
            </div>
          </template>
        </Tab>
        <Tab :label="$t('device-active')" to="/admin/device-alerts/active" class="device-active">
          <div class="table-header">
            <TableCell
              v-for="fieldName in fieldNames"
              ref="sortHeader"
              :key="fieldName"
              :item="fieldName"
              :isHeader="true"
              @cellClick="handleSortClick"
            />
          </div>
          <template v-if="activeTab === 'device-active'">
            <Loading v-if="!siteDevices" class="loader" />
            <div v-else v-for="site in siteDevicesToShow" :key="site.site_id" class="table-row">
              <p class="table-cell text-cell">{{ site.company.name }}</p>
              <p class="table-cell text-cell">{{ site.site }}</p>
              <p class="room-name">{{ site.room }}</p>
              <span
                v-for="([deviceType, device]) in Object.entries(site).filter(
                  ([key, value]) => key !== 'pad_counter' && typeof value === 'object' && !value.name
                  && key !== 'qr_task'
                )"
                :key="deviceType"
                class="device-container"
              >
                <DeviceStatus :status="device.total" isCount />
              </span>
              <p class="table-cell text-cell">{{ site.franchise_name }}</p>
            </div>
            <div class="table-row sum">
              <p>{{ $t('total_sites') }}:</p>
              <p></p>
              <p class="room-name">{{ sitesTotal('room') }}</p>
              <span
                v-for="type in ['pad', 'tablet', 'counter', 'qr_tag', 'sensor']"
                :key="type"
                class="device-container"
              >
                <DeviceStatus :status="sitesTotal(type)" isCount />
              </span>
              <template v-for="idx in 4">
                <p :key="`active-tail-${idx}`"></p>
              </template>
            </div>
          </template>
        </Tab>
        <Tab :label="$t('device-counter')" to="/admin/device-alerts/counter" class="device-counter">
          <div class="table-header">
            <TableCell
              v-for="fieldName in fieldNames"
              ref="sortHeader"
              :key="fieldName"
              :item="fieldName"
              :isHeader="true"
              :isIgnored="fieldName === 'level' ? true : false"
              @cellClick="handleSortClick"
            />
          </div>
          <template v-if="activeTab === 'device-counter'">
            <Loading v-if="!faultyCounters" class="loader" />
            <div v-else v-for="counter in faultyCountersToShow" :key="counter.device_code" class="table-row">
              <p class="table-cell text-cell">{{ counter.company_name }}</p>
              <p class="table-cell text-cell">{{ counter.site_name }}</p>
              <p class="room-name">{{ counter.room_name }}</p>
              <p class="table-cell text-cell">{{ counter.type }}</p>
              <p class="table-cell text-cell">{{ counter.version }}</p>
              <p class="table-cell text-cell">{{ counter.device_code }}</p>
              <p class="table-cell text-cell">{{ counter.counts }}</p>
              <p class="table-cell text-cell">{{ faultyCounterLevels(counter.levels) }}</p>
              <p class="table-cell text-cell">{{ new Date(counter.date).toLocaleDateString('en-gb') }}</p>
              <p class="table-cell text-cell">{{ new Date(counter.lastDate).toLocaleDateString('en-gb') }}</p>
              <img
                :src="require('@/assets/delete-icon.svg')"
                alt="remove-icon"
                class="cell-icon action"
                @click="removeFaultyCounter(counter.device_code)"
              />
            </div>
          </template>
        </Tab>
        <Tab :label="$t('device-search')" to="/admin/device-alerts/search" class="device-search">
          <div class="table-header">
            <TableCell v-for="fieldName in fieldNames" :key="fieldName" :item="fieldName" :isHeader="true" />
          </div>
          <template v-if="activeTab === 'device-search'">
            <Loading v-if="isSearching" class="loader" />
            <div
              v-if="device"
              :key="device.device_code || `${device.company_id}-${device.site_id}`"
              @click="routeToSiteDevices({ company: { id: device.company_id }, site_id: device.site_id })"
              class="table-row"
            >
              <p class="table-cell text-cell">{{ device.company_name }}</p>
              <p class="table-cell text-cell">{{ device.site_name }}</p>
              <p class="room-name">{{ device.room_name }}</p>
              <p>{{ device.type }}</p>
              <p class="table-cell text-cell">{{ device.site_country }}</p>
              <p class="table-cell text-cell">{{ device.site_state }}</p>
              <p class="table-cell text-cell">{{ device.site_city }}</p>
              <p class="table-cell text-cell">{{ device.franchise_name }}</p>
            </div>
          </template>
        </Tab>
      </Tabs>
    </div>
  </section>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import DeviceStatus from '@/modules/devices/components/DeviceStatus'
import Loading from '@/modules/common/components/Loading'
import Tab from '@/modules/common/components/Tabs/Tab'
import Tabs from '@/modules/common/components/Tabs/Tabs'
import TableCell from '@/modules/common/components/customTable/TableCell'
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import Checkbox from '@/modules/common/components/Checkbox'
import sensorService from '../services/sensorService'
import swalService from '../../common/services/swalService'

const SORT_DEVICE_KEYS = ['pad', 'pad_counter', 'tablet', 'counter', 'qr_tag', 'sensor']
const SORT_DATE_KEYS = ['first_date', 'date', 'lastDate']

const isMissingSortValue = (value) => value === null || value === undefined || value === '' || value === 'N/A'

// Missing values always go last; numbers sort high to low, dates oldest first, text A-Z.
function compareSortValues(a, b, isDate) {
  const isAMissing = isMissingSortValue(a)
  const isBMissing = isMissingSortValue(b)
  if (isAMissing || isBMissing) return Number(isAMissing) - Number(isBMissing)
  if (typeof a === 'number' && typeof b === 'number') return isDate ? a - b : b - a
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

export default {
  name: 'DeviceAlerts',
  data() {
    return {
      siteDevices: null,
      faultyCounters: null,
      siteDevicesCopy: null,
      faultyCountersCopy: null,
      sortActive: false,
      searchTxt: '',
      searchBy: '',
      device: null,
      isSearching: false,
    }
  },
  async created() {
    const [faultyCounters, siteDevices] = await Promise.all([
      sensorService.getFaultyCounters(),
      sensorService.getSitesSensorsStatus(),
    ])

    this.faultyCounters = faultyCounters || []
    this.faultyCountersCopy = [...this.faultyCounters]
    this.siteDevices = siteDevices || []

    if (localStorage['pinnedSites']) {
      JSON.parse(localStorage['pinnedSites']).forEach((id) => {
        const idx = this.siteDevices.findIndex(({ site_id }) => site_id === id)
        if (idx === -1) return
        this.siteDevices[idx].isPinned = true
      })
    }

    this.siteDevicesCopy = [...this.siteDevices]
  },
  methods: {
    ...mapMutations(['updateFilter']),
    routeToSiteDevices(site) {
      this.updateFilter({ field: 'company', value: site.company.id })
      this.updateFilter({ field: 'site', value: site.site_id })
      this.$router.push({ name: 'devices' })
    },
    openFullScreen() {
      const deviceAlertsEl = this.$refs.deviceAlerts
      //browser compatibility
      if (deviceAlertsEl.msRequestFullscreen) deviceAlertsEl.msRequestFullscreen()
      else if (deviceAlertsEl.mozRequestFullScreen) deviceAlertsEl.mozRequestFullScreen()
      else if (deviceAlertsEl.webkitRequestFullscreen) deviceAlertsEl.webkitRequestFullscreen()
      else deviceAlertsEl.requestFullscreen()
    },
    handlePin(site, isPinned) {
      site.isPinned = isPinned
      let savedPinnedSitesIDs = JSON.parse(localStorage['pinnedSites'] || null)
      if (!savedPinnedSitesIDs) savedPinnedSitesIDs = []
      if (site.isPinned) savedPinnedSitesIDs.push(site.site_id)
      else savedPinnedSitesIDs = savedPinnedSitesIDs.filter((savedSiteID) => site.site_id !== savedSiteID)
      localStorage['pinnedSites'] = JSON.stringify(savedPinnedSitesIDs)
    },
    handleSortClick(fieldName, isActive) {
      const isCounterTab = this.activeTab === 'device-counter'
      const sourceArray = isCounterTab ? 'faultyCounters' : 'siteDevices'
      const sourceArrayCopy = isCounterTab ? 'faultyCountersCopy' : 'siteDevicesCopy'

      if (!isActive) {
        this[sourceArray] = [...this[sourceArrayCopy]]
        return
      }

      // Header cells keep their own toggle state, so the previously sorted column must be switched off.
      this.resetSortHeaders(fieldName)

      const sortKey = this.getSortKey(fieldName)
      const isDate = SORT_DATE_KEYS.includes(sortKey)
      this[sourceArray] = [...this[sourceArrayCopy]].sort((a, b) =>
        compareSortValues(this.getSortValue(a, sortKey), this.getSortValue(b, sortKey), isDate)
      )
    },
    getSortKey(fieldName) {
      const normalizedField = fieldName
        .split(' ')
        .join('_')
        .toLowerCase()
      const keyMaps = {
        'device-active': { franchise: 'franchise_name' },
        'device-counter': { mac_id: 'device_code', count: 'counts', first_date: 'date', last_date: 'lastDate' },
      }
      const keyMap = keyMaps[this.activeTab] || {}
      return keyMap[normalizedField] || normalizedField
    },
    getSortValue(item, sortKey) {
      const value = item[sortKey]
      if (sortKey === 'company') return value.name
      if (SORT_DEVICE_KEYS.includes(sortKey)) return this.activeTab === 'device-alerts' ? value.err : value.total
      if (SORT_DATE_KEYS.includes(sortKey)) {
        const timestamp = Date.parse(value)
        return Number.isNaN(timestamp) ? null : timestamp
      }
      return value
    },
    resetSortHeaders(exceptFieldName) {
      const headers = this.$refs.sortHeader || []
      headers.forEach((header) => {
        if (header.item !== exceptFieldName) header.sortActive = false
      })
    },
    clearSearch() {
      if (this.$refs.tableSearch) {
        this.$refs.tableSearch.searchTxt = ''
      }
      this.searchTxt = ''
      this.searchBy = ''
      this.device = null
    },
    async handleInput(value, searchBy) {
      const { name } = this.$route

      if (name === 'device-search') {
        if (!value) {
          this.device = null
          return
        }

        this.isSearching = true

        if (this.debouncedFetch) clearTimeout(this.debouncedFetch)

        this.debouncedFetch = setTimeout(async () => {
          this.device = (await sensorService.getByDeviceCode(value)) || null
          this.isSearching = false
        }, 1000)
        return
      }

      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
    },
    faultyCounterLevels(levels) {
      if (!levels[4]) return '3'
      else if (!levels[3]) return '4'
      return '3 & 4'
    },
    async removeFaultyCounter(deviceCode) {
      const result = await swalService.confirmMsg('Are you sure you want to delete this faulty counter?')

      if (!result.value) return

      try {
        await sensorService.removeFaultyCounter(deviceCode)
        swalService.deletedMsg('Faulty counter has been deleted successfully.')
        this.faultyCounters = await sensorService.getFaultyCounters()
        this.faultyCountersCopy = [...this.faultyCounters]
        this.resetSortHeaders()
      } catch (error) {
        swalService.errorMsg()
      }
    },
  },
  computed: {
    ...mapGetters(['filterSelected', 'isRTL']),
    deviceSummary() {
      const emptyType = () => ({ total: 0, offline: 0 })
      const summary = {
        pad: emptyType(),
        pad_counter: emptyType(),
        counter: emptyType(),
        tablet: emptyType(),
      }
      if (!this.siteDevices) return summary

      for (const site of this.siteDevices) {
        for (const type of Object.keys(summary)) {
          summary[type].total += site[type]?.total || 0
          summary[type].offline += site[type]?.err || 0
        }
      }
      return summary
    },
    sitesTotal() {
      return (field) =>
        this.siteDevicesToShow.reduce(
          (sum, device) =>
            field === 'room'
              ? sum + device[field]
              : this.activeTab === 'device-alerts'
              ? sum + device[field].err
              : sum + device[field].total,
          0
        )
    },
    siteDevicesToShow() {
      const pinnedSites = []
      let filteredSites
      if (!this.siteDevices) return []
      if (!this.searchTxt) {
        filteredSites = this.siteDevices.filter((device) => {
          if (this.activeTab === 'device-alerts' && device.isPinned) {
            pinnedSites.push(device)
            return false
          }
          return this.activeTab === 'device-alerts'
            ? device.counter.err || device.pad.err || device.pad_counter.err || device.tablet.err
            : true
        })
        return this.activeTab === 'device-alerts' ? pinnedSites.concat(filteredSites) : filteredSites
      }
      filteredSites = this.siteDevices.filter((device) => {
        if (this.activeTab === 'device-alerts' && device.isPinned) {
          pinnedSites.push(device)
          return false
        }
        if (
          this.activeTab === 'device-active' ||
          ((this.activeTab === 'device-alerts' && device.counter.err) ||
            device.pad.err ||
            device.pad_counter.err ||
            device.tablet.err)
        ) {
          const { searchTxt, searchBy } = this
          if (searchBy === 'company') {
            return device[searchBy].name.toLowerCase().includes(searchTxt.toLowerCase())
          } else {
            if (typeof device[searchBy] === 'number') {
              return device[searchBy].toString() === searchTxt
            } else {
              return device[searchBy].toLowerCase().includes(searchTxt.toLowerCase())
            }
          }
        }
      })
      return this.activeTab === 'device-alerts' ? pinnedSites.concat(filteredSites) : filteredSites
    },
    faultyCountersToShow() {
      if (!this.faultyCounters) return []
      if (!this.searchTxt) return this.faultyCounters
      return this.faultyCounters.filter((counter) => {
        if (this.activeTab === 'device-counter') {
          let { searchTxt, searchBy } = this

          if (searchBy === 'first_date') searchBy = 'date'
          else if (searchBy === 'last_date') searchBy = 'lastDate'

          if (typeof counter[searchBy] === 'number') return counter[searchBy].toString() === searchTxt
          else {
            if (new Date(counter[searchBy]).toString() !== 'Invalid Date')
              new Date(counter[searchBy]).toLocaleDateString('en-gb').includes(searchTxt)
            return counter[searchBy].toLowerCase().includes(searchTxt.toLowerCase())
          }
        }
      })
    },
    fieldNames() {
      const fields = {
        'device-alerts': [
          'pinned',
          'no.',
          'company',
          'site',
          'room',
          'pad',
          'pad_counter',
          'tablet',
          'counter',
          'first_date',
          'site_country',
          'site_state',
          'site_city',
        ],
        'device-active': ['company', 'site', 'room', 'pad', 'tablet', 'counter', 'qr_tag', 'sensor', 'franchise'],
        'device-counter': [
          'company_name',
          'site_name',
          'room_name',
          'type',
          'version',
          'mac_id',
          'count',
          'level',
          'first_date',
          'last_date',
        ],
        'device-search': ['company', 'site', 'room', 'type', 'site_country', 'site_state', 'site_city', 'franchise'],
      }

      return fields[this.activeTab]
    },
    tableTitles() {
      const titles = {
        'device-alerts': ['company', 'site', 'room', 'site_state', 'site_city'],
        'device-active': ['company', 'site', 'franchise_name'],
        'device-counter': ['company_name', 'site_name', 'first_date', 'last_date'],
        'device-search': ['mac_id'],
      }
      return titles[this.activeTab]
    },
    activeTab() {
      return this.$route.name
    },
  },
  watch: {
    '$route.name'() {
      this.clearSearch()
      this.resetSortHeaders()
      if (this.siteDevicesCopy) this.siteDevices = [...this.siteDevicesCopy]
      if (this.faultyCountersCopy) this.faultyCounters = [...this.faultyCountersCopy]
    },
  },
  components: {
    DeviceStatus,
    Loading,
    Tab,
    Tabs,
    TableCell,
    TableSearch,
    Checkbox,
  },
}
</script>

<style lang="scss">
.device-alerts-container .table-row {
  .b-input {
    position: static !important;

    &::after {
      left: 0.5rem !important;
      top: 0.25rem !important;
    }
  }
}
</style>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.pinned {
  background-color: #efeded;
}

.table-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;

  &.text-cell {
    overflow: hidden;

    > span {
      text-overflow: ellipsis;
      overflow: hidden;
      padding-bottom: 10px;
      margin-bottom: -10px;
      white-space: nowrap;

      &:hover {
        overflow: visible;
        white-space: normal;
      }
    }
  }
}

.device-alerts-container {
  min-height: 85vh;
  position: relative;
  overflow-x: auto;

  * {
    font-size: $font-size-regular;
  }

  .device-summary {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr;
    gap: 12px;
    padding: 12px 16px 8px;
    margin-top: 8px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .summary-card {
    border: 1px solid #2b2b2b;
    background: #fff;
    display: flex;
    flex-direction: column;
    min-width: 0;

    &__total,
    &__offline {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 14px;
    }

    &__total {
      border-bottom: 1px solid #2b2b2b;

      .summary-card__label,
      .summary-card__value {
        color: #1a1a1a;
        font-weight: $font-weight-bold;
        font-size: 15px;
      }
    }

    &__offline-row {
      display: grid;
      grid-template-columns: 1fr;
    }

    &--pads .summary-card__offline-row {
      grid-template-columns: 1fr 1fr;

      .summary-card__offline:first-child {
        border-inline-end: 1px solid #2b2b2b;
      }
    }

    &__offline {
      .summary-card__label,
      .summary-card__value {
        color: #b42318;
        font-weight: $font-weight-regular;
        font-size: 13px;
      }
    }

    &__label {
      text-transform: capitalize;
    }

    &__value {
      font-variant-numeric: tabular-nums;
    }
  }

  .table-row:hover {
    .cell-icon {
      visibility: visible;
    }
  }

  .cell-icon {
    visibility: hidden;

    &:hover {
      cursor: pointer;
    }
  }

  .table-header {
    height: 6vh;
    width: 100%;
    border-bottom: 4px solid #e7e7e7;
    display: grid;

    & > * {
      display: flex;
      align-items: center;
      padding-inline: 10px;
      min-width: 0;
    }

    // Keep full header labels visible (was clipping to first letter)
    .table-cell.text-cell {
      overflow: visible;

      > span {
        overflow: visible;
        text-overflow: unset;
        white-space: nowrap;
      }
    }

    p {
      font-weight: $font-weight-regular;
      color: #909090;
    }

    .export-button-container {
      display: flex;
      align-items: center;
      margin-inline-start: 15px;
    }
  }

  .table-row {
    cursor: pointer;
    position: relative;
    height: 5vh;
    width: 100%;
    display: grid;
    border-bottom: 1px solid #e7e7e7;

    .room-name {
      font-weight: $font-weight-bold;
    }

    &.sum {
      cursor: default;
    }

    & > * {
      height: 100%;
      display: flex;
      align-items: center;
      padding-inline: 10px;
      min-width: 0;
    }

    & > img {
      align-self: center;
      height: unset;
      padding: unset;
      padding-inline-start: 15px;
    }

    &:not(.sum):hover {
      background-color: #f5fafe;
    }

    .device-container {
      display: grid;
      grid-template-columns: 0.8fr repeat(3, 1fr);
    }
  }
}

.table-header,
.table-row {
  .device-alerts & {
    // pinned, no., company, site, room, pad, pad_counter, tablet, counter, first_date, country, state, city
    grid-template-columns:
      70px 50px minmax(140px, 1.4fr) minmax(140px, 1.4fr) minmax(80px, 1fr)
      minmax(70px, 0.8fr) minmax(110px, 1.1fr) minmax(80px, 0.9fr) minmax(90px, 0.9fr)
      minmax(100px, 1fr) minmax(110px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr);
    min-width: 1400px;
  }
  .device-active & {
    // company, site, room, pad, tablet, counter, qr_tag, sensor, franchise
    grid-template-columns:
      minmax(160px, 1.3fr) minmax(160px, 1.3fr) minmax(90px, 1fr)
      minmax(70px, 0.8fr) minmax(80px, 0.9fr) minmax(90px, 0.9fr)
      minmax(80px, 0.8fr) minmax(80px, 0.8fr) minmax(120px, 1.1fr);
    min-width: 1100px;
  }
  .device-counter & {
    grid-template-columns: 2fr 2fr 2fr 1fr 0.8fr 2fr 0.8fr 0.8fr 1.2fr 1.2fr 50px;
    min-width: 1200px;
  }
  .device-search & {
    grid-template-columns: minmax(140px, 1fr) minmax(140px, 1fr) minmax(100px, 1fr) repeat(5, minmax(100px, 1fr));
    min-width: 1100px;
  }
}

.tabs-toolbar {
  position: relative;
}

.custom-button-container {
  height: 80px;
  margin: 0;
  display: flex;
  align-items: center;
  position: absolute;
  right: 50px;
  top: 0;
  z-index: 3;
  font-weight: $font-weight-regular;

  @include rtl {
    left: 0;
    right: unset;
  }

  & > * {
    margin: 0 10px;
  }

  span {
    @include modal-input-label;
    margin-bottom: 0px;
    font-size: 16px;
    text-transform: uppercase;
  }

  input {
    @include modal-input;
    margin-inline-start: 5px;
    margin-bottom: 0px;
    padding: 0 5px;
    width: 200px;
  }
}
</style>
